import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { 
  UserGroupIcon, 
  BookOpenIcon, 
  DocumentCheckIcon, 
  ExclamationCircleIcon,
  AcademicCapIcon,
  ChartBarIcon,
  LightBulbIcon,
  UserIcon,
  UsersIcon
} from '@heroicons/react/24/outline';

// Chart.js 등록
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// 출석 상태 정의 (프론트엔드용)
const ATTENDANCE_STATUS = {
  PRESENT: '출석',
  LATE: '지각',
  ABSENT: '결석',
  EXCUSED: '공결'
};

const Dashboard = () => {
  const { user, authAxios } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('week'); // 'day', 'week', 'month'
  const [chartError, setChartError] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [stats, setStats] = useState({
    attendanceCount: 0,
    classesCount: 0,
    instructorCount: 0,
    studentCount: 0,
    attendanceByClass: { labels: [], data: [], rates: [] },
    topClasses: [],
    topStudents: [],
    insights: [
      { id: 1, title: '출석률 증가', description: '지난 주 대비 출석률이 5% 증가했습니다.', type: 'positive' },
      { id: 2, title: '결석 감소', description: '지난 달 대비 결석률이 3% 감소했습니다.', type: 'positive' },
      { id: 3, title: '강의 참여도', description: '웹 개발 강의의 참여도가 가장 높습니다.', type: 'info' }
    ]
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // 백엔드 API에서 대시보드 데이터 가져오기
        const response = await authAxios.get('/dashboard');
        const receivedData = response.data;
        
        // 인기 강의와 우수 학생 데이터 가져오기 (최대 3개)
        const [topClassesResponse, topStudentsResponse] = await Promise.all([
          authAxios.get('/dashboard/top-classes?limit=3'),
          authAxios.get('/dashboard/top-students?limit=3')
        ]).catch(err => {
          console.error('인기 강의/우수 학생 데이터 조회 오류:', err);
          return [{ data: [] }, { data: [] }];
        });

        // 강사 및 학생 수 가져오기
        const [instructorsResponse, studentsResponse] = await Promise.all([
          authAxios.get('/dashboard/users/count?role=instructor'),
          authAxios.get('/dashboard/users/count?role=student')
        ]).catch(err => {
          console.error('강사/학생 수 조회 오류:', err);
          return [{ data: { count: 0 } }, { data: { count: 0 } }];
        });
        
        setStats({
          ...receivedData,
          instructorCount: instructorsResponse.data?.count || 0,
          studentCount: studentsResponse.data?.count || 0,
          topClasses: topClassesResponse.data?.length > 0 ? topClassesResponse.data : [],
          topStudents: topStudentsResponse.data?.length > 0 ? topStudentsResponse.data : [],
          attendanceByClass: { labels: [], data: [], rates: [] }
        });
        
        // 강의별 출석 현황 데이터 가져오기 (인증 필요한 API)
        fetchAttendanceByClass();
        
        setIsLoading(false);
      } catch (err) {
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
        setIsLoading(false);
        console.error('Dashboard data error:', err);
        
        // 오류 발생 시 임시 데이터로 대체 (개발용)
        setStats({
          attendanceCount: 0,
          classesCount: 0,
          instructorCount: 0,
          studentCount: 0,
          attendanceByClass: { labels: [], data: [], rates: [] },
          topClasses: [],
          topStudents: []
        });
      }
    };
    
    const fetchAttendanceByClass = async () => {
      try {
        setChartLoading(true);
        setChartError(null);
        
        const response = await authAxios.get('/dashboard/attendance-by-class');
        const chartData = response.data || { labels: [], data: [], rates: [] };
        
        // 데이터 갱신
        setStats(prevState => ({
          ...prevState,
          attendanceByClass: chartData
        }));
        
        setChartLoading(false);
      } catch (err) {
        console.error('강의별 출석 현황 조회 오류:', err);
        setChartError('강의별 출석 현황을 불러오는 중 오류가 발생했습니다.');
        setChartLoading(false);
      }
    };

    fetchDashboardData();
  }, [authAxios, timeframe]);

  // 막대 차트 옵션
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: '강의별 출석 현황',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const dataIndex = context.dataIndex;
            const rate = stats.attendanceByClass?.rates?.[dataIndex] || 0;
            return [
              `출석 건수: ${context.raw}건`,
              `출석률: ${rate}%`
            ];
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: '출석 건수',
          font: {
            weight: 'bold'
          }
        }
      },
      x: {
        title: {
          display: true,
          text: '강의명',
          font: {
            weight: 'bold'
          }
        }
      }
    }
  };

  // 막대 차트 데이터
  const barChartData = {
    labels: stats.attendanceByClass?.labels || [],
    datasets: [
      {
        label: '출석 건수',
        data: stats.attendanceByClass?.data || [],
        backgroundColor: [
          'rgba(54, 162, 235, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(255, 99, 132, 0.6)',
          'rgba(255, 205, 86, 0.6)',
        ],
        borderColor: [
          'rgb(54, 162, 235)',
          'rgb(75, 192, 192)',
          'rgb(153, 102, 255)',
          'rgb(255, 159, 64)',
          'rgb(255, 99, 132)',
          'rgb(255, 205, 86)',
        ],
        borderWidth: 1,
      },
    ],
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center">
            대시보드
            {stats.activeYear && (
              <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {stats.activeYear}년 데이터
              </span>
            )}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            안녕하세요, {user?.name || '회원'}님! 출결 관리 시스템의 주요 현황입니다.
            {stats.dbName && (
              <span className="ml-2 text-xs text-gray-400">
                (연결 DB: {stats.dbName})
              </span>
            )}
          </p>
        </div>
        {/* <div className="mt-4 md:mt-0">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => setTimeframe('day')}
              className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                timeframe === 'day'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              일간
            </button>
            <button
              type="button"
              onClick={() => setTimeframe('week')}
              className={`px-4 py-2 text-sm font-medium ${
                timeframe === 'week'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border-t border-b border-gray-200 hover:bg-gray-100'
              }`}
            >
              주간
            </button>
            <button
              type="button"
              onClick={() => setTimeframe('month')}
              className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                timeframe === 'month'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              월간
            </button>
          </div>
        </div> */}
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-xl transform transition-all hover:scale-105">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-indigo-100 rounded-md p-3">
                <DocumentCheckIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">총 출석 기록</dt>
                  <dd className="flex items-center">
                    <div className="text-2xl font-semibold text-gray-900">{stats.attendanceCount || 0}건</div>
                  </dd>
                  <dd className="text-xs text-gray-500 mt-1">
                    <span className="text-green-500 font-medium">↑ 12%</span> 지난 {timeframe === 'day' ? '일' : timeframe === 'week' ? '주' : '달'}보다
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-xl transform transition-all hover:scale-105">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-indigo-100 rounded-md p-3">
                <BookOpenIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">총 강의 수</dt>
                  <dd className="flex items-center">
                    <div className="text-2xl font-semibold text-gray-900">{stats.classesCount || 0}개</div>
                  </dd>
                  <dd className="text-xs text-gray-500 mt-1">
                    <span className="text-green-500 font-medium">↑ 5%</span> 지난 {timeframe === 'day' ? '일' : timeframe === 'week' ? '주' : '달'}보다
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-xl transform transition-all hover:scale-105">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-indigo-100 rounded-md p-3">
                <UserIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">총 강사 수</dt>
                  <dd className="flex items-center">
                    <div className="text-2xl font-semibold text-gray-900">{stats.instructorCount || 0}명</div>
                  </dd>
                  <dd className="text-xs text-gray-500 mt-1">
                    <span className="text-green-500 font-medium">↑ 2%</span> 지난 {timeframe === 'day' ? '일' : timeframe === 'week' ? '주' : '달'}보다
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-xl transform transition-all hover:scale-105">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-indigo-100 rounded-md p-3">
                <UsersIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">총 교육생 수</dt>
                  <dd className="flex items-center">
                    <div className="text-2xl font-semibold text-gray-900">{stats.studentCount || 0}명</div>
                  </dd>
                  <dd className="text-xs text-gray-500 mt-1">
                    <span className="text-green-500 font-medium">↑ 8%</span> 지난 {timeframe === 'day' ? '일' : timeframe === 'week' ? '주' : '달'}보다
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 강의별 출석 현황 (막대 차트) - 더 크게 표시 */}
      <div className="bg-white overflow-hidden shadow rounded-xl">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <ChartBarIcon className="h-5 w-5 mr-2 text-indigo-500" />
            강의별 출석 현황
            {user?.roles?.includes('admin') && (
              <span className="ml-2 text-xs font-normal text-gray-500">(전체 강의)</span>
            )}
            {user?.roles?.includes('instructor') && !user?.roles?.includes('admin') && (
              <span className="ml-2 text-xs font-normal text-gray-500">(내 강의)</span>
            )}
          </h3>
          <div className="mt-4" style={{ height: '400px' }}>
            {chartLoading ? (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
            ) : chartError ? (
              <div className="flex justify-center items-center h-full text-red-500">
                <ExclamationCircleIcon className="h-5 w-5 mr-2" />
                {chartError}
              </div>
            ) : stats.attendanceByClass?.labels?.length > 0 ? (
              <Bar options={barChartOptions} data={barChartData} />
            ) : (
              <div className="flex flex-col justify-center items-center h-full text-gray-500">
                <ExclamationCircleIcon className="h-8 w-8 mb-2 text-gray-400" />
                <p>표시할 데이터가 없습니다.</p>
                {user?.roles?.includes('instructor') && !user?.roles?.includes('admin') && (
                  <p className="text-sm mt-2">운영 중인 강의가 없거나 출석 기록이 없습니다.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 인기 강의 및 우수 학생 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* 인기 강의 */}
        <div className="bg-white overflow-hidden shadow rounded-xl">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <BookOpenIcon className="h-5 w-5 mr-2 text-indigo-500" />
              인기 강의
            </h3>
            <div className="mt-4">
              <div className="flow-root">
                <ul className="-my-5 divide-y divide-gray-200">
                  {stats.topClasses && stats.topClasses.length > 0 ? (
                    stats.topClasses.map((classItem, index) => (
                      <li key={index} className="py-4">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-indigo-700 font-semibold">{index + 1}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{classItem.name}</p>
                            <p className="text-sm text-gray-500 truncate">출석 건수: {classItem.count}/{classItem.studentCount}건</p>
                          </div>
                          <div className="inline-flex items-center text-sm font-medium bg-green-100 px-2.5 py-0.5 rounded-full text-green-800">
                            출석률 {classItem.rate}%
                          </div>
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="py-4 text-center text-gray-500">표시할 데이터가 없습니다.</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 우수 학생 */}
        <div className="bg-white overflow-hidden shadow rounded-xl">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <AcademicCapIcon className="h-5 w-5 mr-2 text-indigo-500" />
              우수 학생
            </h3>
            <div className="mt-4">
              <div className="flow-root">
                <ul className="-my-5 divide-y divide-gray-200">
                  {stats.topStudents && stats.topStudents.length > 0 ? (
                    stats.topStudents.map((student, index) => (
                      <li key={index} className="py-4">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-indigo-700 font-semibold">{index + 1}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{student.name}</p>
                            <p className="text-sm text-gray-500 truncate">수강 강의: {student.classCount}개</p>
                          </div>
                          <div className="inline-flex items-center text-sm font-medium bg-green-100 px-2.5 py-0.5 rounded-full text-green-800">
                            출석률 {student.attendanceRate}%
                          </div>
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="py-4 text-center text-gray-500">표시할 데이터가 없습니다.</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 